import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, CaretDown, Globe, Lightning, Shield, Users, Certificate, ChartLine, LinkedinLogo, YoutubeLogo, InstagramLogo, XLogo, Crown } from '@phosphor-icons/react';
import { HOME } from '@/constants/testIds';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import MarketCategoryBar from '@/components/MarketCategoryBar';
import EconomicCalendar from '@/components/EconomicCalendar';
import { getFooterSettings } from '@/utils/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const [tickerData, setTickerData] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [socialLinks, setSocialLinks] = useState({});

  useEffect(() => {
    getFooterSettings()
      .then(r => setSocialLinks(r.data?.social_links || {}))
      .catch((e) => console.debug('Footer settings fetch skipped', e?.message));
  }, []);

  useEffect(() => {
    fetchTickerData();
    // Ticker refresh cadence: 60s (was 5s). Backend quote cache TTL is 120s,
    // so a faster poll would be pure UI churn and does not gain freshness.
    // One BATCH call for all 5 ticker symbols instead of 5 per-symbol calls
    // — halves the round-trip count and lets the backend refresh a stale
    // symbol at most once every 120s across ALL Home viewers.
    const interval = setInterval(fetchTickerData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchTickerData = async () => {
    try {
      const symbols = ['EURUSD', 'GBPUSD', 'BTCUSD', 'XAUUSD', 'AAPL'];
      const r = await axios.get(`${API}/market/quotes?symbols=${encodeURIComponent(symbols.join(','))}`);
      setTickerData(Array.isArray(r.data) ? r.data.filter(q => q && !q.unavailable) : []);
    } catch (e) {
      console.debug('Ticker data fetch skipped', e?.message);
    }
  };

  const features = [
    { icon: Lightning, title: 'Lightning-Fast Execution', description: 'Sub-millisecond order execution with institutional infrastructure' },
    { icon: Shield, title: 'Bank-Grade Security', description: 'Multi-layer encryption and segregated client funds' },
    { icon: Globe, title: 'Global Market Access', description: 'Trade Forex, Stocks, Crypto, Metals, Energy, and Indices' },
    { icon: ChartLine, title: 'Professional Platform', description: 'Advanced charting, technical indicators, and analysis tools' },
    { icon: Users, title: 'Dedicated Support', description: '24/7 expert support from experienced trading professionals' },
    { icon: Certificate, title: 'Transparent Pricing', description: 'Tight spreads, low commissions, no hidden fees' },
  ];

  const faqs = [
    { q: 'How do I open an account?', a: 'Click "Open a Trading Account" and complete the registration. Your account starts at $0.00 — deposit funds via card, wire or crypto to begin trading.' },
    { q: 'What markets can I trade?', a: 'Access Forex, Stocks, Cryptocurrencies, Commodities, Metals, Indices, and Energy from a single platform.' },
    { q: 'Are my funds safe?', a: 'Client funds are held in segregated accounts at tier-1 banking institutions with additional security measures.' },
    { q: 'Is real trading available?', a: 'The platform currently operates in DEMO/SANDBOX mode. Live trading requires regulatory approval.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Sticky Navigation */}
      <nav className="glass-surface sticky top-0 z-50 py-4 px-4 sm:px-6 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-3">
          <div className="text-xl sm:text-2xl font-heading font-black text-brand-primary">WellingtonCDFS</div>
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            <a href="#markets" className="nav-link text-sm font-medium">{t('markets')}</a>
            <a href="#platform" className="nav-link text-sm font-medium">{t('platform')}</a>
            <Link to="/knowledge" className="nav-link text-sm font-medium" data-testid="nav-knowledge-hub">{t('knowledgeHub')}</Link>
            <a href="#who-we-are" className="nav-link text-sm font-medium">{t('whoWeAre')}</a>
            <a href="#faq" className="nav-link text-sm font-medium">{t('faq')}</a>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden" data-testid="language-switcher">
              <button
                onClick={() => setLanguage('en')}
                data-testid="lang-en"
                className={`px-2 sm:px-3 py-1 text-xs font-semibold ${language === 'en' ? 'bg-brand-primary text-white' : 'bg-white text-slate-700'}`}
              >EN</button>
              <button
                onClick={() => setLanguage('it')}
                data-testid="lang-it"
                className={`px-2 sm:px-3 py-1 text-xs font-semibold ${language === 'it' ? 'bg-brand-primary text-white' : 'bg-white text-slate-700'}`}
              >IT</button>
            </div>
            <button data-testid={HOME.loginBtn} onClick={() => navigate('/login')} className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-brand-secondary border border-brand-secondary rounded-lg hover:bg-brand-secondary hover:text-white transition-colors">
              {t('login')}
            </button>
            <button data-testid={HOME.openAccountBtn} onClick={() => navigate('/register')} className="px-3 sm:px-6 py-2 text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-brand-secondary to-brand-accent rounded-lg btn-primary">
              {t('openAccount')}
            </button>
          </div>
        </div>
      </nav>

      {/* Live Market Ticker */}
      <div className="bg-slate-900 text-white py-3 overflow-hidden border-b border-slate-800" data-testid="home-market-ticker">
        <div className="flex items-center gap-8 animate-scroll whitespace-nowrap px-6">
          <span className="text-xs uppercase tracking-widest text-slate-400 font-semibold">{t('liveMarkets')}</span>
          {tickerData.length === 0 ? <span className="text-slate-500 text-sm">Loading market data...</span>
            : [...tickerData, ...tickerData].map((quote, idx) => (
              <div key={idx} className="flex items-center gap-3 text-sm">
                <span className="font-bold">{quote.symbol}</span>
                <span className="text-trading-bullish tabular-nums">{quote.bid?.toFixed(5)}</span>
                <span className="text-trading-bearish tabular-nums">{quote.ask?.toFixed(5)}</span>
                <span className="text-slate-400 text-xs">SPREAD {quote.spread?.toFixed(5)}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-blue-50 -z-10"></div>
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-block px-4 py-1 bg-brand-accent/10 text-brand-accent text-xs uppercase tracking-widest font-bold rounded-full">{t('heroTagline')}</div>
            <h1 data-testid={HOME.heroHeading} className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-slate-900 leading-none">
              {t('heroTitle1')}<br />{t('heroTitle2')}<br /><span className="text-brand-accent">{t('heroTitle3')}</span>
            </h1>
            <p className="text-sm uppercase tracking-widest text-brand-accent font-bold">{t('whoWeAre')}</p>
            <p className="text-lg text-slate-600 leading-relaxed max-w-xl">{t('whoWeAreText')}</p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => navigate('/register')} className="px-8 py-4 font-bold text-white bg-gradient-to-r from-brand-secondary to-brand-accent rounded-lg btn-primary flex items-center gap-2 text-lg">
                {t('openTradingAccount')} <ArrowRight size={20} weight="bold" />
              </button>
              <button onClick={() => navigate('/login')} className="px-8 py-4 font-bold text-brand-secondary border-2 border-brand-secondary rounded-lg hover:bg-brand-secondary hover:text-white transition-colors text-lg">
                {t('exploreWebTrader')}
              </button>
            </div>
          </div>
          <div className="relative">
            <img src="/wellington-hero.png" alt="Wellington investment management" className="rounded-2xl shadow-2xl border border-slate-200 w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>

      {/* Market Category Bar */}
      <div id="markets"><MarketCategoryBar /></div>

      {/* Economic Calendar */}
      <EconomicCalendar />

      {/* Features */}
      <section id="platform" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-widest text-brand-accent font-bold mb-3">Why WellingtonCDFS</div>
            <h2 className="text-4xl font-bold tracking-tight text-slate-900 mb-4">{t('whyTitle')}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl border border-slate-200 hover:shadow-xl transition-all">
                <div className="w-14 h-14 rounded-xl bg-brand-accent/10 flex items-center justify-center mb-4">
                  <feature.icon size={28} weight="duotone" className="text-brand-accent" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WebTrader Multi-Device Showcase */}
      <section id="webtrader-showcase" className="relative py-24 px-6 overflow-hidden bg-gradient-to-b from-[#0a0f1c] via-[#0d1424] to-[#0a0f1c] text-white" data-testid="home-webtrader-showcase">
        {/* Background grid + glow */}
        <div className="absolute inset-0 -z-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(56,189,248,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(16,185,129,0.12), transparent 45%)' }}></div>
        <div className="absolute inset-0 -z-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '48px 48px' }}></div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <div className="text-xs uppercase tracking-[0.25em] text-brand-accent font-bold mb-3">One Platform · Every Device</div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
              Trade anywhere with <span className="text-brand-accent">WellingtonCDFS</span>
            </h2>
            <p className="text-slate-300 text-lg max-w-2xl mx-auto">
              Institutional-grade charting, order execution and market depth — synchronised across your desktop, tablet and phone.
            </p>
          </div>

          {/* Devices stage */}
          <div className="relative mx-auto w-full max-w-6xl" data-testid="webtrader-devices-stage">
            {/* Ambient glow behind devices */}
            <div className="absolute inset-x-0 bottom-0 h-2/3 -z-0 blur-3xl opacity-60" style={{ background: 'radial-gradient(50% 60% at 50% 100%, rgba(56,189,248,0.25), transparent 70%)' }}></div>

            {/* === LAPTOP (Desktop mockup) === */}
            <div className="relative mx-auto w-full max-w-5xl" data-testid="webtrader-laptop">
              {/* Laptop screen bezel */}
              <div className="relative rounded-t-2xl bg-[#111827] border border-slate-700/70 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] overflow-hidden">
                {/* Camera notch */}
                <div className="flex justify-center pt-2 pb-1"><div className="h-1.5 w-1.5 rounded-full bg-slate-600"></div></div>
                {/* Screen content */}
                <div className="bg-[#0b1220] mx-2 mb-2 rounded-md overflow-hidden">
                  {/* Menu bar */}
                  <div className="flex items-center gap-4 px-3 py-1.5 bg-[#0f172a] border-b border-slate-800 text-[10px] text-slate-400">
                    <div className="flex gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500/70"></span><span className="h-2 w-2 rounded-full bg-yellow-500/70"></span><span className="h-2 w-2 rounded-full bg-emerald-500/70"></span></div>
                    <span>File</span><span>View</span><span>Insert</span><span>Charts</span><span>Tools</span>
                    <div className="ml-auto flex items-center gap-2 text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>Live · Demo</div>
                  </div>
                  {/* Toolbar */}
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#0d1424] border-b border-slate-800">
                    {['M1','M5','M15','H1','H4','D1','W1'].map((tf,i)=>(
                      <span key={tf} className={`text-[10px] px-2 py-0.5 rounded ${i===3?'bg-brand-accent text-white':'text-slate-400 hover:text-white'}`}>{tf}</span>
                    ))}
                    <span className="mx-2 h-4 w-px bg-slate-700"></span>
                    <span className="text-[10px] text-slate-400">EURUSD · H4</span>
                    <div className="ml-auto flex items-center gap-3 text-[10px]">
                      <span className="text-slate-400">Bid <span className="text-trading-bullish tabular-nums">1.08421</span></span>
                      <span className="text-slate-400">Ask <span className="text-trading-bearish tabular-nums">1.08434</span></span>
                    </div>
                  </div>
                  {/* Body: symbols panel + chart */}
                  <div className="grid grid-cols-12 h-[280px] sm:h-[340px] lg:h-[400px]">
                    {/* Symbols panel */}
                    <div className="col-span-3 border-r border-slate-800 bg-[#0a1120] p-2 space-y-1 overflow-hidden">
                      <div className="text-[9px] uppercase tracking-widest text-slate-500 pb-1">Market Watch</div>
                      {[
                        ['EURUSD','1.08421','1.08434','up'],
                        ['GBPUSD','1.26518','1.26532','up'],
                        ['USDJPY','149.812','149.826','down'],
                        ['XAUUSD','2043.20','2043.60','up'],
                        ['BTCUSD','67420.5','67445.2','up'],
                        ['ETHUSD','3542.10','3543.60','down'],
                        ['AAPL','185.42','185.48','up'],
                        ['TSLA','241.15','241.32','down'],
                        ['USOIL','78.34','78.42','up'],
                      ].map(([s,b,a,dir])=> (
                        <div key={s} className="flex items-center justify-between text-[10px] py-0.5 border-b border-slate-800/50">
                          <span className="font-semibold text-slate-200">{s}</span>
                          <span className={`tabular-nums ${dir==='up'?'text-trading-bullish':'text-trading-bearish'}`}>{b}</span>
                        </div>
                      ))}
                    </div>
                    {/* Chart area */}
                    <div className="col-span-9 relative bg-[#0b1220]">
                      {/* Grid */}
                      <svg viewBox="0 0 600 340" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
                        <defs>
                          <pattern id="grid" width="40" height="34" patternUnits="userSpaceOnUse">
                            <path d="M 40 0 L 0 0 0 34" fill="none" stroke="#1f2937" strokeWidth="0.5" />
                          </pattern>
                          <linearGradient id="ema" x1="0" x2="1" y1="0" y2="0">
                            <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.9" />
                            <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.6" />
                          </linearGradient>
                        </defs>
                        <rect width="600" height="340" fill="url(#grid)" />
                        {/* Candlesticks */}
                        {[
                          [30,170,140,180,'up'],[50,150,120,160,'up'],[70,180,150,190,'down'],[90,140,110,150,'up'],
                          [110,120,90,130,'up'],[130,160,130,175,'down'],[150,150,120,160,'up'],[170,130,100,140,'up'],
                          [190,110,80,120,'up'],[210,140,110,150,'down'],[230,160,130,175,'down'],[250,175,145,185,'down'],
                          [270,155,120,165,'up'],[290,130,100,140,'up'],[310,110,80,120,'up'],[330,95,65,105,'up'],
                          [350,120,85,130,'down'],[370,140,110,150,'down'],[390,125,95,135,'up'],[410,105,75,115,'up'],
                          [430,85,55,95,'up'],[450,75,45,85,'up'],[470,95,65,105,'down'],[490,115,85,125,'down'],
                          [510,100,70,110,'up'],[530,80,50,90,'up'],[550,65,35,75,'up'],[570,55,25,65,'up'],
                        ].map(([x,hi,lo,close,dir],i)=>{
                          const color = dir==='up'?'#22c55e':'#ef4444';
                          const open = dir==='up'?close+15:close-15;
                          const top = Math.min(open, close);
                          const bh = Math.abs(open-close);
                          return (
                            <g key={i}>
                              <line x1={x} x2={x} y1={hi} y2={lo} stroke={color} strokeWidth="1" />
                              <rect x={x-4} y={top} width="8" height={bh} fill={color} />
                            </g>
                          );
                        })}
                        {/* EMA line */}
                        <polyline fill="none" stroke="url(#ema)" strokeWidth="1.5" points="20,175 60,165 100,150 140,160 180,140 220,155 260,140 300,120 340,110 380,125 420,100 460,90 500,105 540,80 580,60" />
                        {/* Price scale */}
                        <g fontSize="9" fill="#64748b" fontFamily="ui-monospace, monospace">
                          <text x="580" y="40">1.09210</text>
                          <text x="580" y="100">1.08820</text>
                          <text x="580" y="160">1.08434</text>
                          <text x="580" y="220">1.08050</text>
                          <text x="580" y="280">1.07660</text>
                        </g>
                        {/* Current price marker */}
                        <g>
                          <line x1="0" x2="580" y1="160" y2="160" stroke="#22c55e" strokeDasharray="2,3" strokeWidth="0.8" opacity="0.5" />
                          <rect x="555" y="152" width="45" height="14" fill="#22c55e" />
                          <text x="577" y="162" fontSize="9" fill="#0b1220" textAnchor="middle" fontFamily="ui-monospace, monospace" fontWeight="bold">1.08434</text>
                        </g>
                      </svg>
                      {/* Order panel overlay */}
                      <div className="absolute top-3 right-3 w-32 bg-[#0f172a]/90 border border-slate-700 rounded p-2 backdrop-blur">
                        <div className="text-[9px] text-slate-400 mb-1">One-click trade</div>
                        <div className="grid grid-cols-2 gap-1">
                          <button className="text-[10px] font-bold py-1 rounded bg-rose-600/90 text-white">SELL</button>
                          <button className="text-[10px] font-bold py-1 rounded bg-sky-600/90 text-white">BUY</button>
                        </div>
                        <div className="mt-1 text-[9px] text-slate-500 flex justify-between"><span>Lot</span><span className="text-white">0.10</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Laptop base */}
              <div className="mx-auto h-3 w-[105%] -translate-x-[2.5%] rounded-b-2xl bg-gradient-to-b from-slate-700 to-slate-900 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.5)]"></div>
              <div className="mx-auto h-1 w-16 -mt-1 rounded-b-full bg-slate-800"></div>
            </div>

            {/* === TABLET (bottom-right, overlapping) === */}
            <div className="hidden sm:block absolute right-[-2%] lg:right-[-4%] bottom-[-8%] w-[36%] max-w-[280px] rotate-[6deg]" data-testid="webtrader-tablet">
              <div className="rounded-2xl bg-[#0f172a] border border-slate-700 p-1.5 shadow-2xl">
                <div className="bg-[#0b1220] rounded-lg overflow-hidden">
                  {/* Tablet top bar */}
                  <div className="flex items-center justify-between px-2 py-1 bg-[#0f172a] border-b border-slate-800 text-[8px]">
                    <span className="text-slate-400">≡ TSLA</span>
                    <span className="text-slate-300 tabular-nums">241.32 <span className="text-trading-bullish">+1.24%</span></span>
                    <span className="text-brand-accent">Save</span>
                  </div>
                  {/* Chart */}
                  <div className="relative h-32 bg-[#0b1220]">
                    <svg viewBox="0 0 200 130" preserveAspectRatio="none" className="w-full h-full">
                      <rect width="200" height="130" fill="url(#grid)" opacity="0.6"/>
                      {[[10,70,50],[25,60,40],[40,80,55],[55,55,35],[70,45,25],[85,65,45],[100,55,35],[115,40,20],[130,55,30],[145,70,45],[160,50,30],[175,35,15],[190,25,10]].map(([x,hi,lo],i)=>{
                        const dir = i%3===0?'#ef4444':'#22c55e';
                        return <g key={i}><line x1={x} x2={x} y1={hi} y2={lo} stroke={dir} strokeWidth="0.8"/><rect x={x-2} y={(hi+lo)/2-4} width="4" height="8" fill={dir}/></g>;
                      })}
                      <polyline fill="none" stroke="#38bdf8" strokeWidth="1" points="10,55 40,50 70,40 100,45 130,35 160,30 190,20"/>
                    </svg>
                  </div>
                  {/* Timeframes */}
                  <div className="flex items-center gap-1 px-2 py-1 bg-[#0f172a] text-[7px] text-slate-500">
                    {['1D','5D','1M','3M','6M','1Y','ALL'].map((t,i)=>(<span key={t} className={i===2?'text-brand-accent':''}>{t}</span>))}
                  </div>
                </div>
              </div>
            </div>

            {/* === PHONE (front-right, overlapping) === */}
            <div className="absolute left-1/2 sm:left-auto sm:right-[18%] lg:right-[22%] -translate-x-1/2 sm:translate-x-0 bottom-[-14%] sm:bottom-[-12%] w-[42%] sm:w-[24%] max-w-[200px] -rotate-[4deg] z-20" data-testid="webtrader-phone">
              <div className="rounded-[2rem] bg-black border-[6px] border-slate-800 p-1 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.8)]">
                <div className="bg-[#0b1220] rounded-[1.5rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="flex justify-between items-center px-4 pt-2 pb-1 text-[8px] text-white">
                    <span className="font-semibold">9:41</span>
                    <div className="h-3 w-10 bg-black rounded-b-xl -mt-1"></div>
                    <span>●●● ▮</span>
                  </div>
                  {/* Header */}
                  <div className="px-3 py-2 border-b border-slate-800">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] font-bold text-white">GOLD · XAUUSD</div>
                        <div className="text-[8px] text-slate-500">Spot Metal</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold text-trading-bullish tabular-nums">2043.60</div>
                        <div className="text-[8px] text-trading-bullish">+0.82%</div>
                      </div>
                    </div>
                  </div>
                  {/* Mini chart */}
                  <div className="relative h-28 bg-[#0b1220]">
                    <svg viewBox="0 0 200 110" preserveAspectRatio="none" className="w-full h-full">
                      <defs>
                        <linearGradient id="phoneFill" x1="0" x2="0" y1="0" y2="1">
                          <stop offset="0%" stopColor="#22c55e" stopOpacity="0.35"/>
                          <stop offset="100%" stopColor="#22c55e" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      <path d="M0,80 L20,70 L40,75 L60,55 L80,60 L100,40 L120,45 L140,30 L160,35 L180,20 L200,15 L200,110 L0,110 Z" fill="url(#phoneFill)"/>
                      <polyline fill="none" stroke="#22c55e" strokeWidth="1.5" points="0,80 20,70 40,75 60,55 80,60 100,40 120,45 140,30 160,35 180,20 200,15"/>
                      {/* Volume bars */}
                      {[85,90,80,95,88,92,86,94,89,96].map((y,i)=>(<rect key={i} x={i*20+2} y={y} width="16" height={110-y} fill="#334155" opacity="0.6"/>))}
                    </svg>
                  </div>
                  {/* Buy/Sell */}
                  <div className="grid grid-cols-2 gap-1 p-2 bg-[#0f172a]">
                    <button className="flex flex-col items-center py-1.5 rounded bg-rose-600/90 text-white">
                      <span className="text-[7px] uppercase tracking-widest">Sell</span>
                      <span className="text-[10px] font-bold tabular-nums">2043.20</span>
                    </button>
                    <button className="flex flex-col items-center py-1.5 rounded bg-sky-600 text-white">
                      <span className="text-[7px] uppercase tracking-widest">Buy</span>
                      <span className="text-[10px] font-bold tabular-nums">2043.60</span>
                    </button>
                  </div>
                  {/* Bottom nav */}
                  <div className="flex justify-around py-1.5 bg-[#0b1220] border-t border-slate-800 text-[7px] text-slate-500">
                    <span>Quotes</span><span className="text-brand-accent">Chart</span><span>Trade</span><span>History</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature strip below devices */}
          <div className="mt-32 sm:mt-40 grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { title: 'Desktop WebTrader', desc: 'Full charting with 40+ indicators, DOM ladder and one-click execution.' },
              { title: 'Tablet Ready', desc: 'Every timeframe, every symbol — optimised for touch and split-screen.' },
              { title: 'Mobile Trading', desc: 'Manage positions, set alerts and trade Gold, FX and Crypto on the go.' },
            ].map((f,i)=> (
              <div key={i} className="p-5 rounded-xl bg-white/[0.03] border border-white/10 backdrop-blur">
                <div className="text-brand-accent text-xs uppercase tracking-widest font-bold mb-2">0{i+1}</div>
                <div className="font-bold text-white mb-1">{f.title}</div>
                <div className="text-sm text-slate-400">{f.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <button onClick={() => navigate('/register')} className="px-8 py-4 font-bold text-white bg-gradient-to-r from-brand-secondary to-brand-accent rounded-lg btn-primary flex items-center gap-2 text-lg" data-testid="showcase-open-account-btn">
              Launch WebTrader Now <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* Account Types — public, pre-registration. Mirrors the SAME
          tier boundaries that drive the logged-in Dashboard tier card:
            200 ≤ bal < 5,000      → Standard
            5,000 ≤ bal < 50,000   → Premium
            50,000 ≤ bal < 250,000 → Platinum
            bal ≥ 250,000          → VIP
          Currency is intentionally neutral here (visitor has no account
          yet). All amounts shown WITHOUT a $/€ sign and labelled
          "Available in USD / EUR" to avoid implying one or the other. */}
      <section id="account-types" className="py-24 px-6 bg-white" data-testid="home-account-types-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <div className="text-xs uppercase tracking-widest text-brand-accent font-bold mb-3">Choose Your Tier</div>
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900">Account Types</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-sm">
              Four deposit ranges to fit every trader. Figures below reflect account balance ranges only — not guaranteed returns, profits or earnings.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { key: "Standard", range: "200 – 4,999", from: 200, to: 4999,
                desc: "Entry-level account for new traders exploring the platform.",
                accent: "bg-white border-slate-200" },
              { key: "Premium", range: "5,000 – 49,999", from: 5000, to: 49999,
                desc: "For active traders who want sustained access to the full instrument universe.",
                accent: "bg-white border-brand-accent" },
              { key: "Platinum", range: "50,000 – 249,999", from: 50000, to: 249999,
                desc: "Higher-volume traders seeking enhanced platform access.",
                accent: "bg-gradient-to-b from-white to-slate-100 border-slate-300" },
              { key: "VIP", range: "250,000+", from: 250000, to: null,
                desc: "Premium clients. Elevated account standing.",
                accent: "bg-gradient-to-br from-brand-primary to-brand-accent text-white border-transparent" },
            ].map((tier, i) => {
              const isVip = tier.key === "VIP";
              return (
                <div
                  key={tier.key}
                  data-testid={`home-account-tier-${tier.key.toLowerCase()}`}
                  className={`relative p-6 rounded-2xl border-2 ${tier.accent} flex flex-col transition-shadow hover:shadow-xl ${isVip ? "shadow-2xl" : ""}`}
                >
                  {isVip && (
                    <div className="absolute -top-3 right-4 bg-white text-brand-primary text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border border-brand-primary">
                      Premium Tier
                    </div>
                  )}
                  <div className={`flex items-center gap-2 text-xs uppercase tracking-widest font-bold mb-3 ${isVip ? "text-white/80" : "text-brand-accent"}`}>
                    {isVip && <Crown size={14} weight="fill" />}
                    {tier.key}
                  </div>
                  <div className={`text-3xl font-black mb-1 ${isVip ? "text-white" : "text-slate-900"}`}>
                    {tier.range}
                  </div>
                  <div className={`text-xs mb-5 ${isVip ? "text-white/70" : "text-slate-500"}`}>
                    Available in USD / EUR
                  </div>
                  <p className={`text-sm leading-relaxed flex-1 ${isVip ? "text-white/85" : "text-slate-600"}`}>
                    {tier.desc}
                  </p>
                  <button
                    onClick={() => navigate('/register')}
                    data-testid={`home-tier-cta-${tier.key.toLowerCase()}`}
                    className={`mt-6 w-full px-5 py-3 rounded-lg font-bold text-sm transition-colors ${
                      isVip
                        ? "bg-white text-brand-primary hover:bg-slate-100"
                        : "bg-slate-900 text-white hover:bg-brand-accent"
                    }`}
                  >
                    Open Account
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-slate-400 mt-8 max-w-3xl mx-auto">
            Deposit range determines the tier label on your Dashboard. Tiers do not imply any guaranteed profit, bonus, or trading outcome.
          </p>
        </div>
      </section>

      {/* Knowledge Hub Callout */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand-accent font-bold mb-3">Education Center</div>
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-slate-900">{t('khTitle')}</h2>
            <p className="text-lg text-slate-600 mb-8">{t('khSubtitle')}</p>
            <button onClick={() => navigate('/knowledge')} className="px-8 py-4 font-bold text-white bg-gradient-to-r from-brand-secondary to-brand-accent rounded-lg btn-primary" data-testid="home-explore-knowledge-btn">
              Explore Knowledge Hub
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {['basicsOfTrading', 'technicalAnalysis', 'fundamentalAnalysis', 'traderPsychology'].map(k => (
              <div key={k} className="p-6 bg-slate-50 rounded-xl border border-slate-200 hover:border-brand-accent transition-colors cursor-pointer" onClick={() => navigate('/knowledge')}>
                <div className="font-bold text-slate-800">{t(k)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900">{t('faq')}</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="bg-white border border-slate-200 rounded-lg">
                <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full p-6 flex justify-between items-center text-left hover:bg-slate-50" data-testid={`faq-toggle-${idx}`}>
                  <span className="font-semibold text-slate-900">{faq.q}</span>
                  <CaretDown size={20} className={`transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === idx && <div className="px-6 pb-6 text-slate-600">{faq.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-brand-primary to-brand-secondary text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6">{t('startTradingToday')}</h2>
          <p className="text-xl mb-8 text-slate-200">{t('demoBalanceCta')}</p>
          <button onClick={() => navigate('/register')} className="px-10 py-5 bg-white text-brand-primary font-bold rounded-lg hover:shadow-2xl text-lg">{t('createFreeAccount')}</button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-6" data-testid="site-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="text-2xl font-heading font-black mb-4">
                <a href="/" className="hover:text-brand-accent transition-colors" data-testid="footer-brand-link">WellingtonCDFS</a>
              </div>
              <p className="text-slate-400 text-sm mb-4">Professional online trading platform for global markets.</p>
              <div className="space-y-1 text-sm">
                <div className="text-slate-500 text-xs uppercase tracking-wider">Contact</div>
                <a href="mailto:info@wellingtoncdfs.eu" className="block text-slate-300 hover:text-brand-accent" data-testid="footer-email">info@wellingtoncdfs.eu</a>
                <a href="tel:+390237901395" className="block text-slate-300 hover:text-brand-accent" data-testid="footer-phone">+39 02 3790 1395</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4 uppercase tracking-wider text-white">{t('termsAndConditionsTitle')}</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><span className="hover:text-brand-accent cursor-pointer">{t('investmentRisks')}</span></li>
                <li><span className="hover:text-brand-accent cursor-pointer">{t('termsOfUse')}</span></li>
                <li><span className="hover:text-brand-accent cursor-pointer">{t('dataProtectionNotice')}</span></li>
                <li><span className="hover:text-brand-accent cursor-pointer">{t('cookiePolicy')}</span></li>
                <li><span className="hover:text-brand-accent cursor-pointer">{t('proxyVoting')}</span></li>
                <li><span className="hover:text-brand-accent cursor-pointer">SFDR</span></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 uppercase tracking-wider text-white">{t('followUsSocial')}</h4>
              <ul className="space-y-3 text-sm text-slate-300" data-testid="footer-social-list">
                {socialLinks.linkedin && (
                  <li>
                    <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer"
                       data-testid="footer-social-linkedin"
                       className="flex items-center gap-3 hover:text-brand-accent transition-colors">
                      <LinkedinLogo size={22} weight="fill" aria-label="LinkedIn" />
                      <span>LinkedIn</span>
                    </a>
                  </li>
                )}
                {socialLinks.youtube && (
                  <li>
                    <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                       data-testid="footer-social-youtube"
                       className="flex items-center gap-3 hover:text-brand-accent transition-colors">
                      <YoutubeLogo size={22} weight="fill" aria-label="YouTube" />
                      <span>YouTube</span>
                    </a>
                  </li>
                )}
                {socialLinks.instagram && (
                  <li>
                    <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                       data-testid="footer-social-instagram"
                       className="flex items-center gap-3 hover:text-brand-accent transition-colors">
                      <InstagramLogo size={22} weight="fill" aria-label="Instagram" />
                      <span>Instagram</span>
                    </a>
                  </li>
                )}
                {socialLinks.twitter && (
                  <li>
                    <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer"
                       data-testid="footer-social-twitter"
                       className="flex items-center gap-3 hover:text-brand-accent transition-colors">
                      <XLogo size={22} weight="fill" aria-label="X (Twitter)" />
                      <span>X / Twitter</span>
                    </a>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 uppercase tracking-wider text-white">{t('contactsTitle')}</h4>
              <ul className="space-y-2 text-sm text-slate-300">
                <li><span className="hover:text-brand-accent cursor-pointer">{t('contactTeam')}</span></li>
                <li><span className="hover:text-brand-accent cursor-pointer">{t('officesWorldwide')}</span></li>
                <li><a href="mailto:info@wellingtoncdfs.eu" className="hover:text-brand-accent">{t('contactUs')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-xs text-slate-500 leading-relaxed">
            <p className="whitespace-pre-line max-w-4xl mx-auto" data-testid="footer-rights">{t('rights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
